import React from 'react';
import styled, { css } from 'styled-components';
import { horizonGlow, cloudDrift, withMotion } from '../styles/animations';
import { theme } from '../styles/theme';

const AviationBackground = () => (
  <Wrapper aria-hidden="true">
    {/* Deep sky gradient */}
    <SkyGradient />

    {/* Subtle horizon glow */}
    <HorizonGlow />

    {/* Faint altitude grid lines */}
    <GridLines>
      {[15, 28, 42, 56, 70, 84].map((pct) => (
        <GridLine key={pct} style={{ top: `${pct}%` }} />
      ))}
    </GridLines>

    {/* Slow drifting cloud layer */}
    <CloudLayer />
    <CloudLayer delay="18s" opacity="0.04" />

    {/* Subtle perspective runway lines */}
    <RunwayLines>
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Vanishing-point perspective lines from bottom-center */}
        {[-600, -340, -140, 0, 140, 340, 600].map((offset, i) => (
          <line
            key={i}
            x1={720 + offset}
            y1={900}
            x2={720}
            y2={540}
            stroke="rgba(59,130,246,0.06)"
            strokeWidth="1"
          />
        ))}
        {/* Horizontal altitude bands */}
        {[560, 610, 660, 710, 760].map((y, i) => (
          <line
            key={i}
            x1={0}
            y1={y}
            x2={1440}
            y2={y}
            stroke="rgba(59,130,246,0.04)"
            strokeWidth="1"
          />
        ))}
      </svg>
    </RunwayLines>
  </Wrapper>
);

const Wrapper = styled.div`
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
`;

const SkyGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    175deg,
    ${theme.colors.cockpitBlack} 0%,
    ${theme.colors.aviationNavy} 40%,
    ${theme.colors.backgroundSecondary} 100%
  );
`;

const HorizonGlow = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 55%;
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(59,130,246,0.22) 30%,
    rgba(6,182,212,0.18) 50%,
    rgba(59,130,246,0.22) 70%,
    transparent 100%
  );

  ${withMotion(css`
    animation: ${horizonGlow} 6s ease-in-out infinite;
  `)}
`;

const GridLines = styled.div`
  position: absolute;
  inset: 0;
`;

const GridLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(30,45,69,0.5) 20%,
    rgba(30,45,69,0.5) 80%,
    transparent 100%
  );
`;

const CloudLayer = styled.div`
  position: absolute;
  top: 20%;
  left: -10%;
  width: 120%;
  height: 30%;
  background: radial-gradient(
    ellipse 60% 40% at 50% 50%,
    rgba(20,30,46,${({ opacity }) => opacity || '0.06'}) 0%,
    transparent 70%
  );

  ${({ delay }) =>
    withMotion(css`
      animation: ${cloudDrift} 80s ${delay || '0s'} linear infinite;
    `)}
`;

const RunwayLines = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.8;
`;

export default AviationBackground;
